import { useMutation, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { roadTaxAPI, vehicleAPI } from '../../api/index';
import { queryClient } from '../../App';
import { PaymentMode, RoadTax, Vehicle } from '../../types/index';
import { Button } from '../Button';

interface AddRoadTaxModalProps {
  open: boolean
  onClose: () => void
  customerId: string
  roadTax?: RoadTax
  onSuccess?: () => void
}

type TaxType = 'new_registration' | 'renewal' | 'transfer' | 'temporary'
type TaxPeriod = 'quarterly' | 'half_yearly' | 'annual' | 'lifetime'

const taxTypes: TaxType[] = ['new_registration', 'renewal', 'transfer', 'temporary']
const taxPeriods: TaxPeriod[] = ['quarterly', 'half_yearly', 'annual', 'lifetime']
const paymentModes: PaymentMode[] = ['online', 'cash', 'dd', 'cheque', 'neft']

export default function AddRoadTaxModal({
  open,
  onClose,
  customerId,
  roadTax,
  onSuccess,
}: AddRoadTaxModalProps) {
  const [formData, setFormData] = useState({
    vehicle_id: '',
    receipt_number: '',
    tax_type: '' as TaxType | '',
    tax_period: '' as TaxPeriod | '',
    amount_paid: '',
    penalty_amount: '',
    total_amount: '',
    payment_date: '',
    valid_from: '',
    valid_until: '',
    is_lifetime: false,
    state: '',
    payment_mode: '' as PaymentMode | '',
    bank_reference: '',
    notes: '',
  })

  // Fetch vehicles for the customer
  const { data: vehiclesData, isLoading: isLoadingVehicles } = useQuery({
    queryKey: ['vehicles', customerId],
    queryFn: () => vehicleAPI.listForCustomer(customerId),
    enabled: open,
  })

  useEffect(() => {
    if (roadTax) {
      setFormData({
        vehicle_id: roadTax.vehicle_id || '',
        receipt_number: roadTax.receipt_number || '',
        tax_type: roadTax.tax_type || '',
        tax_period: roadTax.tax_period || '',
        amount_paid: roadTax.amount_paid || '',
        penalty_amount: roadTax.penalty_amount || '',
        total_amount: roadTax.total_amount || '',
        payment_date: roadTax.payment_date || '',
        valid_from: roadTax.valid_from || '',
        valid_until: roadTax.valid_until || '',
        is_lifetime: roadTax.is_lifetime || false,
        state: roadTax.state || '',
        payment_mode: roadTax.payment_mode || '',
        bank_reference: roadTax.bank_reference || '',
        notes: roadTax.notes || '',
      })
    } else {
      setFormData({
        vehicle_id: '',
        receipt_number: '',
        tax_type: '',
        tax_period: '',
        amount_paid: '',
        penalty_amount: '',
        total_amount: '',
        payment_date: '',
        valid_from: '',
        valid_until: '',
        is_lifetime: false,
        state: '',
        payment_mode: '',
        bank_reference: '',
        notes: '',
      })
    }
  }, [roadTax, open])

  // Auto-calculate total amount
  useEffect(() => {
    const paid = parseFloat(formData.amount_paid) || 0
    const penalty = parseFloat(formData.penalty_amount) || 0
    setFormData((prev) => ({
      ...prev,
      total_amount: (paid + penalty).toString(),
    }))
  }, [formData.amount_paid, formData.penalty_amount])

  const createMutation = useMutation({
    mutationFn: () =>
      roadTaxAPI.create(customerId, {
        ...formData,
        amount_paid: parseFloat(formData.amount_paid) || 0,
        penalty_amount: formData.penalty_amount ? parseFloat(formData.penalty_amount) : 0,
        total_amount: parseFloat(formData.total_amount) || 0,
        tax_type: (formData.tax_type || null) as any,
        tax_period: (formData.tax_period || null) as any,
        payment_date: formData.payment_date || null,
        valid_from: formData.valid_from || null,
        valid_until: formData.is_lifetime ? null : (formData.valid_until || null),
        payment_mode: (formData.payment_mode || null) as any,
        state: formData.state,
        bank_reference: formData.bank_reference || null,
        notes: formData.notes || null,
      } as any),
    onSuccess: () => {
      toast.success('Road tax created')
      queryClient.invalidateQueries({ queryKey: ['road_taxes'] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ?? 'Failed to create road tax',
      )
    },
  })

  const updateMutation = useMutation({
    mutationFn: () =>
      roadTaxAPI.update(roadTax!.id, {
        receipt_number: formData.receipt_number || undefined,
        amount_paid: formData.amount_paid ? parseFloat(formData.amount_paid) : undefined,
        penalty_amount: formData.penalty_amount ? parseFloat(formData.penalty_amount) : undefined,
        total_amount: formData.total_amount ? parseFloat(formData.total_amount) : undefined,
        tax_type: (formData.tax_type || undefined) as any,
        tax_period: (formData.tax_period || undefined) as any,
        payment_date: formData.payment_date || undefined,
        valid_from: formData.valid_from || undefined,
        valid_until: formData.is_lifetime ? undefined : (formData.valid_until || undefined),
        payment_mode: (formData.payment_mode || undefined) as any,
        state: formData.state || undefined,
        bank_reference: formData.bank_reference || undefined,
        notes: formData.notes || undefined,
      } as any),
    onSuccess: () => {
      toast.success('Road tax updated')
      queryClient.invalidateQueries({ queryKey: ['road_taxes'] })
      onClose()
      onSuccess?.()
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.error ?? 'Failed to update road tax',
      )
    },
  })

  const isLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    isLoadingVehicles
  const isEdit = !!roadTax

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !formData.vehicle_id.trim() ||
      !formData.receipt_number.trim() ||
      !formData.tax_type ||
      !formData.tax_period ||
      !formData.amount_paid.trim() ||
      !formData.payment_date.trim() ||
      !formData.valid_from.trim() ||
      !formData.state.trim()
    ) {
      toast.error(
        'Vehicle, receipt number, tax type, tax period, amount, payment date, valid from, and state are required',
      )
      return
    }
    if (!formData.is_lifetime && !formData.valid_until) {
      toast.error('Valid until date is required (unless it is a lifetime tax)')
      return
    }
    if (isEdit) {
      updateMutation.mutate()
    } else {
      createMutation.mutate()
    }
  }

  if (!open) return null

  const vehicles = vehiclesData?.data?.vehicles || []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white">
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEdit ? 'Edit Road Tax' : 'Add Road Tax'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Vehicle *
              </label>
              <select
                required
                disabled={isLoading || isEdit}
                value={formData.vehicle_id}
                onChange={(e) =>
                  setFormData({ ...formData, vehicle_id: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select vehicle</option>
                {Array.isArray(vehicles) && vehicles.map((vehicle: Vehicle) => (
                  <option key={vehicle.id} value={vehicle.id}>
                    {vehicle.registration_number} - {vehicle.make} {vehicle.model}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Receipt Number *
              </label>
              <input
                type="text"
                required
                disabled={isLoading}
                value={formData.receipt_number}
                onChange={(e) =>
                  setFormData({ ...formData, receipt_number: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="REC-2024-001"
              />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Tax Type *
              </label>
              <select
                required
                disabled={isLoading}
                value={formData.tax_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tax_type: e.target.value as TaxType | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select tax type</option>
                {taxTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Tax Period *
              </label>
              <select
                required
                disabled={isLoading}
                value={formData.tax_period}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tax_period: e.target.value as TaxPeriod | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select tax period</option>
                {taxPeriods.map((period) => (
                  <option key={period} value={period}>
                    {period.replace(/_/g, ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 3 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Amount Paid *
              </label>
              <input
                type="number"
                required
                disabled={isLoading}
                value={formData.amount_paid}
                onChange={(e) =>
                  setFormData({ ...formData, amount_paid: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Penalty Amount
              </label>
              <input
                type="number"
                disabled={isLoading}
                value={formData.penalty_amount}
                onChange={(e) =>
                  setFormData({ ...formData, penalty_amount: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Total Amount
              </label>
              <input
                type="text"
                disabled
                value={formData.total_amount}
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 bg-gray-100 text-gray-600"
              />
            </div>
          </div>

          {/* Row 4 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Payment Date
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.payment_date}
                onChange={(e) =>
                  setFormData({ ...formData, payment_date: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Valid From
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.valid_from}
                onChange={(e) =>
                  setFormData({ ...formData, valid_from: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
          </div>

          {/* Row 5 - Valid Until and Is Lifetime */}
          {!formData.is_lifetime && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Valid Until
              </label>
              <input
                type="date"
                disabled={isLoading}
                value={formData.valid_until}
                onChange={(e) =>
                  setFormData({ ...formData, valid_until: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              />
            </div>
          )}

          {/* Row 6 - Lifetime Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isLifetime"
              disabled={isLoading}
              checked={formData.is_lifetime}
              onChange={(e) =>
                setFormData({ ...formData, is_lifetime: e.target.checked })
              }
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="isLifetime" className="text-sm font-medium text-gray-700">
              Is Lifetime Tax
            </label>
          </div>

          {/* Row 7 */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                State *
              </label>
              <input
                type="text"
                required
                disabled={isLoading}
                value={formData.state}
                onChange={(e) =>
                  setFormData({ ...formData, state: e.target.value })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
                placeholder="Maharashtra"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Payment Mode
              </label>
              <select
                disabled={isLoading}
                value={formData.payment_mode}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    payment_mode: e.target.value as PaymentMode | '',
                  })
                }
                className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              >
                <option value="">Select payment mode</option>
                {paymentModes.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 8 */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Bank Reference
            </label>
            <input
              type="text"
              disabled={isLoading}
              value={formData.bank_reference}
              onChange={(e) =>
                setFormData({ ...formData, bank_reference: e.target.value })
              }
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              placeholder="Bank Reference Number"
            />
          </div>

          {/* Row 9 - Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              disabled={isLoading}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded-lg border border-gray-200 px-4 py-2 disabled:bg-gray-100 focus:border-saffron focus:outline-none"
              placeholder="Any additional notes"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <Button
              type="submit"
              isLoading={isLoading}
              disabled={isLoading}
              variant="primary"
            >
              {isEdit ? 'Update Road Tax' : 'Add Road Tax'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
